import { useQuery } from "convex/react";
import { Fragment } from "react";
import { NavLink } from "react-router";
import { Badge } from "@/components/ui/badge";
import {
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/components/ui/sidebar";
import { BUCKET_ORDER, bucketFor, type DateBucket } from "@/lib/dateBuckets";
import { cn } from "@/lib/utils";
import { api } from "../../../convex/_generated/api";
import type { DerivedThreadKind } from "../../../convex/lib/threadKind";

type DerivedKind = DerivedThreadKind;

const KIND_LABEL: Record<DerivedKind, string> = {
	chat: "chat",
	email: "email",
	teams: "teams",
	cli: "cli",
	other: "other",
};

const KIND_BADGE_CLASS: Record<DerivedKind, string> = {
	chat: "bg-primary/15 text-primary",
	email: "bg-chart-2/15 text-chart-2",
	teams: "bg-chart-3/15 text-chart-3",
	cli: "bg-chart-4/15 text-chart-4",
	other: "bg-muted text-muted-foreground",
};

const OUTCOME_DOT_CLASS: Record<string, string> = {
	cancelled_by_user: "bg-muted-foreground",
	timed_out: "bg-warning",
	failed: "bg-destructive",
	crashed: "bg-destructive",
};

type ThreadRow = NonNullable<
	ReturnType<typeof useQuery<typeof api.agent.queries.listAllThreadsStaff>>
>[number];

export default function AllThreadsSection({
	activeThreadId,
}: {
	activeThreadId: string | undefined;
}) {
	const threads = useQuery(api.agent.queries.listAllThreadsStaff);

	// Loading or unauthorized — render nothing; user's own chats already show above.
	if (threads === undefined) return null;
	if (threads.length === 0) return null;

	// Buckets match the per-user "My chats" grouping above; BUCKET_ORDER fixes
	// render order, and lastActiveAt desc sort is preserved within each bucket.
	const grouped = new Map<DateBucket, ThreadRow[]>();
	for (const t of threads) {
		const ts = t.lastActiveAt ?? t._creationTime;
		const bucket = bucketFor(ts);
		const arr = grouped.get(bucket) ?? [];
		arr.push(t);
		grouped.set(bucket, arr);
	}

	return (
		<>
			<SidebarGroup className="group-data-[collapsible=icon]:hidden">
				<SidebarGroupLabel>All threads (staff)</SidebarGroupLabel>
			</SidebarGroup>
			{BUCKET_ORDER.filter((b) => grouped.has(b)).map((bucket) => (
				<SidebarGroup
					key={bucket}
					className="group-data-[collapsible=icon]:hidden"
				>
					<SidebarGroupLabel>{bucket}</SidebarGroupLabel>
					<SidebarGroupContent>
						<SidebarMenu>
							{grouped.get(bucket)?.map((t) => {
								const isActive = activeThreadId === t.clericalThreadId;
								const ownerLabel =
									t.ownerName?.trim() ||
									t.ownerEmail?.split("@")[0] ||
									"unknown";
								const title = t.title ?? t.clericalThreadId;
								const outcomeClass = t.lastRunOutcome
									? OUTCOME_DOT_CLASS[t.lastRunOutcome]
									: undefined;
								return (
									<Fragment key={t._id}>
										<SidebarMenuItem>
											<SidebarMenuButton
												render={
													<NavLink
														to={`/chat/${encodeURIComponent(t.clericalThreadId)}`}
													/>
												}
												isActive={isActive}
												tooltip={`${ownerLabel} — ${title}`}
												className="h-auto py-1.5"
											>
												<div className="flex min-w-0 flex-1 flex-col gap-0.5">
													<div className="flex items-center gap-1.5">
														<Badge
															variant="ghost"
															className={cn(
																"h-4 px-1.5 text-[10px] font-mono",
																KIND_BADGE_CLASS[t.derivedKind],
															)}
														>
															{KIND_LABEL[t.derivedKind]}
														</Badge>
														{outcomeClass ? (
															<span
																className={cn(
																	"size-1.5 rounded-full",
																	outcomeClass,
																)}
																title={t.lastRunOutcome ?? ""}
															/>
														) : null}
														<span className="truncate text-[11px] text-muted-foreground">
															{ownerLabel}
														</span>
													</div>
													<span className="truncate text-xs">{title}</span>
												</div>
											</SidebarMenuButton>
										</SidebarMenuItem>
									</Fragment>
								);
							})}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			))}
		</>
	);
}
